// Profile Page - User profile, stats, settings
import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Avatar,
  Stack,
  Divider,
  Button,
  Card,
  CardContent,
  Grid,
} from '@mui/material';
import { motion } from 'framer-motion';
import {
  Person,
  Edit,
  Logout,
  Settings,
  Bookmark,
  Favorite,
  StarRate,
  TrendingUp,
} from '@mui/icons-material';
import { useAuth } from '../../hooks/useAuth';

export default function Profile() {
  const navigate = useNavigate();
  const { user, profile, logout } = useAuth();

  const handleSignOut = async () => {
    await logout();
    navigate('/');
  };

  const stats = [
    { label: 'Favorites', value: profile?.favorites?.length || 0, icon: <Favorite color="error" /> },
    { label: 'Bookmarks', value: profile?.bookmarks?.length || 0, icon: <Bookmark color="primary" /> },
    { label: 'Watch History', value: profile?.watchHistory?.length || 0, icon: <TrendingUp color="success" /> },
  ];

  return (
    <Box sx={{ width: '100%', maxWidth: 900, mx: 'auto' }}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Avatar
            src={user?.photoURL || undefined}
            sx={{
              width: 120,
              height: 120,
              mx: 'auto',
              mb: 2,
              fontSize: 48,
              fontWeight: 800,
              background: 'var(--anime-gradient-primary)',
              border: '3px solid var(--anime-token-border-default)',
            }}
          >
            {(user?.displayName || user?.email || 'U').charAt(0).toUpperCase()}
          </Avatar>
          <Typography variant="h4" fontWeight={800}>
            {user?.displayName || 'Guest User'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {user?.email || 'Sign in to sync your data across devices'}
          </Typography>
        </Box>

        {/* Stats */}
        <Grid container spacing={2} sx={{ mb: 5 }}>
          {stats.map((stat, i) => (
            <Grid item xs={4} key={stat.label}>
              <Card>
                <CardContent sx={{ textAlign: 'center', py: 3 }}>
                  <Box sx={{ mb: 1 }}>{stat.icon}</Box>
                  <Typography variant="h4" fontWeight={800}>{stat.value}</Typography>
                  <Typography variant="caption" color="text.secondary">{stat.label}</Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Divider sx={{ mb: 4 }} />

        {/* Actions */}
        <Stack spacing={2}>
          <Button
            variant="contained"
            size="large"
            fullWidth
            startIcon={<Settings />}
            onClick={() => navigate('/settings')}
          >
            Settings
          </Button>
          <Button
            variant="outlined"
            size="large"
            fullWidth
            startIcon={<Bookmark />}
            onClick={() => navigate('/watchlist')}
          >
            My Watchlist
          </Button>
          <Button
            variant="outlined"
            size="large"
            fullWidth
            color="error"
            startIcon={<Logout />}
            onClick={handleSignOut}
          >
            Sign Out
          </Button>
        </Stack>

        {/* Account Info */}
        <Box sx={{ mt: 5 }}>
          <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>Account Information</Typography>
          <Stack spacing={1.5}>
            {[
              { label: 'Display Name', value: user?.displayName || 'Not set' },
              { label: 'Email', value: user?.email || 'Guest mode' },
              { label: 'Member Since', value: user?.metadata?.creationTime ? new Date(user.metadata.creationTime).toLocaleDateString() : 'Unknown' },
            ].map(row => (
              <Box key={row.label} sx={{ display: 'flex', justifyContent: 'space-between', py: 1, borderBottom: '1px solid var(--anime-token-divider)' }}>
                <Typography variant="body2" color="text.secondary">{row.label}</Typography>
                <Typography variant="body2" fontWeight={500}>{row.value}</Typography>
              </Box>
            ))}
          </Stack>
        </Box>
      </motion.div>
    </Box>
  );
}